import JsonSchema, { JsonSchemaType } from '@evidentid/json-schema/interfaces/JsonSchema';
import { TprmSchema, TprmSchemaDisplayFormat } from '@evidentid/tprm-portal-lib/models/common';

import { COUNTRIES_ISO_3166_1 } from '@/modules/entity-management/constants/countryCodes';
import { getAddressPropOrder } from '@/modules/entity-management/utils/get-address-props-order/getAddressPropOrder';

const addressSchema: TprmSchema = {
    type: JsonSchemaType.object,
    title: 'Address',
    $schema: 'http://json-schema.org/draft-07/schema#',
    // When an address is present, only `country` is unconditionally required.
    // `state` is required only for US/CA (see if/then/else below), mirroring the
    // custom-property Address schema. street/city/zipCode/aptSuiteOrUnit are optional.
    // An absent/null address is handled by the parent form's optional logic
    // (the address field itself is not in profile's required array).
    required: [ 'country' ],
    properties: {
        country: {
            enum: [ ...COUNTRIES_ISO_3166_1.map((x) => x.code) ],
            enumLabels: [ ...COUNTRIES_ISO_3166_1.map((x) => x.label || x.name) ],
            title: 'Country',
            maxLength: 2,
            minLength: 1,
            displayFormat: TprmSchemaDisplayFormat.country,
        } as TprmSchema,
        street: { type: JsonSchemaType.string, title: 'Street Address', minLength: 1 },
        aptSuiteOrUnit: { type: JsonSchemaType.string, title: 'Apt, Suite or Unit', minLength: 1 },
        city: { type: JsonSchemaType.string, title: 'City', minLength: 1 },
        state: { type: JsonSchemaType.string, title: 'State', minLength: 1 },
        zipCode: { type: JsonSchemaType.string, title: 'Zip', minLength: 1 },
    },
    if: {
        required: [ 'country' ],
        properties: {
            country: { enum: [ 'US', 'CA' ] },
        },
    },
    then: {
        required: [ 'state' ],
        properties: {},
    },
    else: {
        required: [],
        properties: {},
    },
    displayFormat: TprmSchemaDisplayFormat.address,
    propertiesOrder: getAddressPropOrder(),
} as TprmSchema;

export const entitySchemas: Record<string, JsonSchema> = {
    // Bulk import only. A value updates that entity, a blank cell creates a new one.
    id: {
        type: JsonSchemaType.string,
        title: 'Entity ID',
        description: 'The unique identifier of an existing entity. Leave blank to create a new entity.',
        placeholder: 'Leave blank to create a new entity',
    },
    displayName: {
        type: JsonSchemaType.string,
        minLength: 1,
        title: 'Display Name',
        description: 'The display name is a custom name unique to this entity.',
        placeholder: 'Type Entity Display Name',
        // FIXME: a proper way to validate trimmed string for empty check
        pattern: '^(?!\\s+$)',
    },
    legalName: {
        type: JsonSchemaType.string,
        title: 'Legal Name',
        description: 'The legal business name is the official name of the entity that appears on legal and government forms.',
        placeholder: 'Type Entity Legal Name',
        pattern: '^(?!\\s+$)',
    },
    doingBusinessAs: {
        type: JsonSchemaType.array,
        title: 'DBA Name(s)',
        addItemTitle: 'ADD DBA',
        items: {
            type: JsonSchemaType.string,
            description: 'The “doing business as” (DBA) name is a different name the entity uses publicly instead of the legal name. ',
            placeholder: 'Type Entity Doing Business Name',
            minLength: 1,
            pattern: '^(?!\\s+$)',
        } as JsonSchema,
    },
    contactEmail: {
        type: JsonSchemaType.string,
        format: 'email',
        minLength: 1,
        title: 'Primary Contact Email',
        placeholder: 'Type entity email',
    },
    contactName: {
        type: JsonSchemaType.string,
        title: 'Primary Contact Name',
        placeholder: 'Type contact name',
    },
    contactPhoneNumber: {
        type: JsonSchemaType.string,
        format: 'phone',
        title: 'Primary Contact Phone Number',
    },
    address: addressSchema,
};
